import { Box, Divider, Typography } from "@mui/material";
import { useCallback, useMemo, useReducer } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { ValidDataService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import useDialogModal from "../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import validLookup from "../../../../utils/validLookup";
import ValidFrameInputForm from "./validFrameInputForm";
import ValidFrameEditForm from "./validFrameEditForm";
import PrintLabelsDialog from "./PrintLabelsDialog";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import { useValidInventoryColumns } from "../useValidInventoryColumns";
//import EditValidFrameForm from "./editValidFrameForm";

export default function Frames(props) {
    const [ValidFrameInputDialog, showValidFrameInputForm] = useDialogModal(ValidFrameInputForm);
    const [ValidFrameEditDialog, showValidFrameEditForm] = useDialogModal(ValidFrameEditForm);
    const [LabelsDialog, showLabelsDialog] = useDialogModal(PrintLabelsDialog);
    const columns = useValidInventoryColumns('frame');

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidFrameInputForm();
    }

    const handleRowEdit = (rowData) => {
        showValidFrameEditForm(null, { recordToEdit: rowData });
    }

    // Note: copy handler intentionally removed due to being unused. Reintroduce if enabling copy action.

    /**
    * fetch data
    */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        updateState({ loading: true });
        ValidDataService.getInventoryDataWithLimit(from, pageSize, filters, sortBy, 'frame', signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [signal]);

    const deleteRecordHandler = (rowids) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const deleteRecordResult = await ValidDataService.deleteInventoryRecord(rowids.map(x => parseInt(x, 10)));
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);
                    const updateList = state.data.filter(d => !rowids.map(x => parseInt(x, 10)).includes(d.id));
                    updateState({ data: updateList });
                } else {
                    toast.success("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = (newRecord) => {
        const newData = state.data.concat(newRecord);
        updateState({ data: newData });
    }

    const recordEditHandler = (record) => {
        // find the frame by row id
        const existingValidFrame = state.data;
        const idx = existingValidFrame.findIndex(e => e.id === record.id);
        const updatesList = existingValidFrame.map((row, index) => {
            if (row.id === record.id) {
                return {
                    ...existingValidFrame[idx], ...record,
                }
            }
            return row
        });

        // update list
        updateState({
            data: updatesList
        });
    }

        // Helper: print labels for selected frames using a hidden iframe (no new tab)
    const printFrameLabels = useCallback((rows, options = {}) => {
                if (!rows || rows.length === 0) return;
        const { template = '5160', startPosition = 1, includePrice = true, includeBarcode = true, quantityPerItem = 1 } = options;
                const cfg = template === '5167' ? { columns: 4, perSheet: 80 } : { columns: 3, perSheet: 30 };

                const padCount = Math.max(0, Math.min(cfg.perSheet - 1, parseInt(startPosition, 10) - 1 || 0));
                const placeholders = new Array(padCount).fill(null);
        const q = Math.max(1, parseInt(quantityPerItem, 10) || 1);
        const expandedRows = rows.flatMap(r => Array.from({ length: q }, () => r));

                const html = `<!DOCTYPE html>
                <html>
                    <head>
                        <meta charset="utf-8" />
                        <title>Frame Labels</title>
                        <style>
                            @page { margin: 10mm; }
                            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 12px; }
                            .grid { display: grid; grid-template-columns: repeat(${cfg.columns}, 1fr); gap: 8px; }
                            .label { border: 1px solid #ccc; padding: 8px; border-radius: 6px; }
                            .model { font-weight: 600; font-size: 12px; }
                            .sku { font-size: 11px; color: #444; }
                            .price { font-size: 12px; margin-top: 4px; }
                            @media print { .label { page-break-inside: avoid; } }
                            .barcode { width: 100%; height: 28px; }
                        </style>
                        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    </head>
                    <body>
                        <div class="grid">
                            ${[...placeholders, ...expandedRows].map(r => {
                                if (!r) {
                                    return `<div class="label"></div>`;
                                }
                                const model = r.modelNumber || r.model || '';
                                const sku = r.sku || r.internalUpc || '';
                                const price = r.listPrice || r.price || '';
                                return `<div class="label">
                                    <div class="model">${model}</div>
                                    <div class="sku">SKU: ${sku}</div>
                                    ${includePrice ? `<div class="price">${price ? `$${price}` : ''}</div>` : ''}
                                    ${includeBarcode && sku ? `<svg class="barcode" jsbarcode-format="CODE128" jsbarcode-value="${sku}" jsbarcode-textmargin="0" jsbarcode-fontoptions="bold"></svg>` : ''}
                                </div>`;
                            }).join('')}
                        </div>
                        <script>
                          try {
                            if (window.JsBarcode) {
                              JsBarcode('.barcode').init();
                            }
                          } catch (e) { console.error('Barcode init failed', e); }
                        </script>
                    </body>
                </html>`;

                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                document.body.appendChild(iframe);

                const doc = iframe.contentWindow || iframe.contentDocument;
                const iDoc = doc.document || doc;
                iDoc.open();
                iDoc.write(html);
                iDoc.close();

                setTimeout(() => {
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    } catch (e) {
                        console.error('Print failed:', e);
                    } finally {
                        setTimeout(() => {
                            try { document.body.removeChild(iframe); } catch(_) {}
                        }, 1000);
                    }
                }, 200);
        }, []);

    const selectedToolbarActions = useMemo(() => ([
        {
            id: 'print-labels',
            label: 'Print Labels',
            icon: <PrintIcon />,
            tooltip: 'Print labels for selected frames',
            variant: 'button',
            onClick: (selectedRows, { clearSelection }) => {
                showLabelsDialog((opts) => {
                    printFrameLabels(selectedRows, opts || {});
                    clearSelection && clearSelection();
                }, { rows: selectedRows });
            }
        }
    ]), [printFrameLabels, showLabelsDialog]);

    return (
        <Box>
            <Typography variant="h4">Frames</Typography>
            <Divider sx={{ mb: 2 }} />
            <EnhancedTable
                columns={columns}
                addRecordHandler={null}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                selectedToolbarActions={selectedToolbarActions}
                actions={[
                    {
                        icon: <EditIcon />,
                        onClick: handleRowEdit
                    },
                    // {
                    //     icon: <ContentCopy />,
                    //     onClick: handleRowCopy
                    // },
                ]}
            />
            <ValidFrameInputDialog onRecordAdded={recordAddHandler}/>
            <ValidFrameEditDialog onRecordUpdated={recordEditHandler} onRecordCopied={recordAddHandler} />
            <LabelsDialog />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}